import { ForwardedRef, forwardRef, InputHTMLAttributes } from "react";
import styles from "./PhoneNumberInput.module.scss";
import useForwardRef from "../../../../hooks/useForwardRef";
import { CountrySelector, defaultCountries, parseCountry, usePhoneInput } from "react-international-phone";
import { CaretDownIcon } from "../../../../assets/icons";

type Props = {
	value?: string;
	error?: string;
	required?: boolean;
	disabled?: boolean;
	disableFlagSelector?: boolean;
	inputProps?: InputHTMLAttributes<HTMLInputElement>;
	placeholder?: string;
	backgroundColor?: string;
	onChange?: (value: string) => void;
};

const PhoneNumberInput = forwardRef((props: Props, ref: ForwardedRef<HTMLInputElement>) => {
	const { value, inputProps, required, disabled, disableFlagSelector = false, onChange, backgroundColor } = props;
	const forwardedRef = useForwardRef(ref);

	const allowedCountryCodes = ['ng', 'gh', 'ke', 'zm']; // Nigeria, Ghana, Kenya, Zambia
	const allowedCountries = defaultCountries.filter((country) => {
		const countryData = parseCountry(country);
		return allowedCountryCodes.includes(countryData.iso2);
	});

	const handleContainerClick = () => {
		if (forwardedRef?.current) {
			forwardedRef.current.focus();
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			handleContainerClick();
		}
	};

	const phoneInput = usePhoneInput({
		defaultCountry: "zm",
		value: value,
		defaultMask: "... ... ....",
		forceDialCode: true,
        countries: allowedCountries,
		onChange: data => {
			if (onChange) {
				onChange(data.phone);
			}
		},
	});

	const containerStyle = backgroundColor ? { backgroundColor } : undefined;

	return (
		<div
			className={`${styles.inputContainer} ${props.error && styles.errorInput}`}
			onClick={handleContainerClick}
			onKeyDown={handleKeyDown} // Keyboard interaction handler
			role="button"
			tabIndex={0}
			aria-label="Phone number input"
			style={containerStyle}
		>
			<div className={`${styles.inputIcon} ${styles.orangeBg}`}>
				<CountrySelector
					selectedCountry={phoneInput.country.iso2}
					onSelect={(country) => phoneInput.setCountry(country.iso2)}
					disabled={disableFlagSelector}
					countries={allowedCountries}
					dropdownArrowStyle={{ display: "none" }}
					renderButtonWrapper={renderButtonWrapper}
					flagStyle={{ width: "20px", height: "20px", margin: 0 }}
				/>
			</div>
			<div className={styles.inputContent}>
				<span className={styles.inputLabel}>Phone Number {required && <div className={styles.asterix}>&nbsp;*</div>}</span>
				<input {...inputProps} value={phoneInput.inputValue} type="tel" disabled={disabled} onChange={phoneInput.handlePhoneValueChange} ref={forwardedRef} />
				{props.error && <div className={styles.error}>{props.error}</div>}
			</div>
		</div>
	);

	function renderButtonWrapper(props: { children: React.ReactNode; rootProps: any }) {
		const { children, rootProps } = props;
		return (
			<button {...rootProps} 
				className={`${rootProps.className?? ''} ${styles.buttonWrapper}`}
				type="button"
				style={{
					...rootProps.style,
				}}>
				{children}
				<div className={styles.dropdownContainer}>
				 <CaretDownIcon color="red" />
				</div>
			</button>
		);
	}
});

export default PhoneNumberInput;
